import PropTypes from "prop-types";
import "./EmptyState.css";

const DEFAULT_ICON = (
  <svg
    viewBox="0 0 64 64"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="8" y="12" width="48" height="40" rx="4" />
    <path d="M8 24h48" />
    <path d="M20 32h24" />
    <path d="M20 40h16" />
  </svg>
);

/**
 * Reusable empty / no-data state component.
 * Use for: no results, no venues, no vendors, empty lists, etc.
 *
 * @param {string} [title] - Short heading (default: "No data available")
 * @param {string} [message] - Descriptive text (default: generic message)
 * @param {React.ReactNode} [icon] - Optional icon or illustration (default: inbox-style icon)
 * @param {React.ReactNode} [action] - Optional button or link (e.g. "Try another city")
 * @param {string} [className] - Extra class for the wrapper
 * @param {string} [variant] - "default" | "compact" for spacing
 */
function EmptyState({
  title = "No data available",
  message = "There's nothing to show here yet. Try a different filter or check back later.",
  icon,
  action,
  className = "",
  variant = "default",
}) {
  const showIcon = icon !== null && icon !== false;
  const iconNode = showIcon ? (icon || DEFAULT_ICON) : null;

  return (
    <div
      className={`empty-state empty-state--${variant} ${className}`.trim()}
      role="status"
      aria-live="polite"
    >
      {iconNode && <div className="empty-state-icon">{iconNode}</div>}
      <h3 className="empty-state-title">{title}</h3>
      <p className="empty-state-message">{message}</p>
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

EmptyState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.oneOf(["default", "compact"]),
};

export default EmptyState;
